<template>
  <Teleport to="body">
    <Transition name="slide-right">
      <div v-if="visible" class="trace-panel" @click.stop>
        <div class="trace-panel-header">
          <span class="trace-panel-title">溯源详情</span>
          <button class="trace-panel-close" @click="$emit('update:visible', false)">×</button>
        </div>
        <div class="trace-panel-body">
          <!-- Search Overview -->
          <div class="trace-overview" v-if="msg">
            <div class="trace-stat">
              <span class="stat-label">来源数量</span>
              <span class="stat-value">{{ msg.knowledgeTraces?.length || 0 }}</span>
            </div>
            <div class="trace-stat" v-if="msg?.knowledgeTraces?.[0]?.searchMethod">
              <span class="stat-label">检索方法</span>
              <span class="stat-value-sm">{{ msg.knowledgeTraces[0].searchMethod }}</span>
            </div>
            <div class="trace-stat" v-if="msg?.knowledgeTraces?.[0]?.searchDurationMs != null">
              <span class="stat-label">检索耗时</span>
              <span class="stat-value-sm">{{ msg.knowledgeTraces[0].searchDurationMs }}ms</span>
            </div>
            <div class="trace-stat">
              <span class="stat-label">综合置信度</span>
              <span class="stat-value" :class="'text-' + overallConfidenceLevel(msg)">
                {{ (overallConfidenceValue(msg) * 100).toFixed(1) }}%
              </span>
            </div>
          </div>

          <!-- Tabs -->
          <el-tabs v-model="activeTabModel" class="trace-tabs">
            <el-tab-pane label="问答对" name="qa">
              <div v-for="(trace, tIdx) in qaTraces" :key="'qa-'+tIdx"
                   class="trace-detail-card"
                   :class="{ 'trace-card-active': tIdx === activeIndex }"
                   :ref="el => { if (tIdx === activeIndex && el) el.scrollIntoView({ behavior: 'smooth', block: 'center' }) }">
                <div class="trace-card-header">
                  <span class="trace-badge" :class="'badge-' + (trace.confidenceLevel || 'medium')">{{ tIdx + 1 }}</span>
                  <el-tag :type="confidenceTagType(trace.confidenceLevel)" size="small">
                    {{ confidenceLabel(trace.confidenceLevel) }}
                  </el-tag>
                  <div class="trace-card-actions">
                    <el-button v-if="trace.knowledgeId" type="primary" link size="small" @click="$emit('navigate-knowledge', trace)">
                      <el-icon><FolderOpened /></el-icon>
                      <span>知识库</span>
                    </el-button>
                    <el-button v-if="qaRecordId(trace)" type="success" link size="small"
                               @click="$emit('view-source', { ...trace, qaId: qaRecordId(trace) })">
                      <el-icon><Document /></el-icon>
                      <span>源数据</span>
                    </el-button>
                  </div>
                </div>

                <!-- 知识库来源 -->
                <div class="trace-source-bar" v-if="trace.knowledgeName">
                  <span class="trace-source-label">来源知识库：</span>
                  <el-link type="primary" :underline="false" @click="$emit('navigate-knowledge', trace)">
                    {{ trace.knowledgeName }}
                    <el-icon class="el-icon--right"><ArrowRight /></el-icon>
                  </el-link>
                </div>

                <div class="trace-section-block">
                  <div class="trace-block-label">问题</div>
                  <div class="trace-block-content">{{ trace.question }}</div>
                </div>

                <div class="trace-section-block">
                  <div class="trace-block-label">答案</div>
                  <div class="trace-block-content trace-answer">{{ trace.answer }}</div>
                </div>

                <div class="trace-section-block">
                  <div class="trace-block-label">置信度分析</div>
                  <div class="confidence-bar">
                    <div class="confidence-bar-fill"
                         :class="'confidence-' + (trace.confidenceLevel || 'medium')"
                         :style="{ width: (trace.confidence * 100) + '%' }">
                    </div>
                    <span class="confidence-text">{{ (trace.confidence * 100).toFixed(1) }}%</span>
                  </div>
                </div>

                <div class="trace-section-block">
                  <div class="trace-block-label">匹配详情</div>
                  <div class="trace-meta-grid">
                    <div class="trace-meta-item">
                      <span class="meta-label">匹配方式</span>
                      <span class="meta-value">{{ matchSourceLabel(trace.matchSource) }}</span>
                    </div>
                    <div class="trace-meta-item" v-if="trace.vectorScore != null">
                      <span class="meta-label">向量相似度</span>
                      <span class="meta-value">{{ trace.vectorScore.toFixed(4) }}</span>
                    </div>
                    <div class="trace-meta-item" v-if="trace.rrfRank != null">
                      <span class="meta-label">RRF排序</span>
                      <span class="meta-value">#{{ trace.rrfRank }}</span>
                    </div>
                    <div class="trace-meta-item" v-if="trace.vectorWeight != null">
                      <span class="meta-label">向量权重</span>
                      <span class="meta-value">{{ (trace.vectorWeight * 100).toFixed(0) }}%</span>
                    </div>
                    <div class="trace-meta-item" v-if="trace.bm25Weight != null">
                      <span class="meta-label">BM25权重</span>
                      <span class="meta-value">{{ (trace.bm25Weight * 100).toFixed(0) }}%</span>
                    </div>
                    <div class="trace-meta-item" v-if="trace.graphWeight != null">
                      <span class="meta-label">图谱权重</span>
                      <span class="meta-value">{{ (trace.graphWeight * 100).toFixed(0) }}%</span>
                    </div>
                  </div>
                </div>

                <div class="trace-section-block" v-if="trace.category || trace.keywords">
                  <div class="trace-block-label">知识库信息</div>
                  <div class="trace-meta-grid">
                    <div class="trace-meta-item" v-if="trace.category">
                      <span class="meta-label">分类</span>
                      <span class="meta-value">{{ trace.category }}</span>
                    </div>
                    <div class="trace-meta-item" v-if="trace.keywords">
                      <span class="meta-label">关键词</span>
                      <span class="meta-value">{{ trace.keywords }}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div v-if="!qaTraces || qaTraces.length === 0" class="trace-empty">
                <el-icon><ChatDotRound /></el-icon>
                <span>暂无问答对引用</span>
              </div>
            </el-tab-pane>

            <el-tab-pane label="文档" name="doc">
              <div v-for="(trace, tIdx) in docTraces" :key="'doc-'+tIdx"
                   class="trace-detail-card">
                <div class="trace-card-header">
                  <span class="trace-badge badge-medium">{{ tIdx + 1 }}</span>
                  <el-tag :type="confidenceTagType(trace.confidenceLevel)" size="small">
                    {{ confidenceLabel(trace.confidenceLevel) }}
                  </el-tag>
                  <div class="trace-card-actions">
                    <el-button v-if="trace.knowledgeId" type="primary" link size="small" @click="$emit('navigate-knowledge', trace)">
                      <el-icon><FolderOpened /></el-icon>
                      <span>知识库</span>
                    </el-button>
                    <el-button v-if="trace.documentId" type="success" link size="small" @click="handleViewDocument(trace)">
                      <el-icon><Document /></el-icon>
                      <span>查看原文</span>
                    </el-button>
                  </div>
                </div>

                <div class="trace-source-bar" v-if="trace.documentName">
                  <span class="trace-source-label">来源文档：</span>
                  <el-link type="primary" :underline="false" @click="handleViewDocument(trace)">
                    {{ trace.documentName }}
                    <el-icon class="el-icon--right"><ArrowRight /></el-icon>
                  </el-link>
                </div>

                <div class="trace-source-bar" v-if="trace.knowledgeName">
                  <span class="trace-source-label">来源知识库：</span>
                  <el-link type="primary" :underline="false" @click="$emit('navigate-knowledge', trace)">
                    {{ trace.knowledgeName }}
                    <el-icon class="el-icon--right"><ArrowRight /></el-icon>
                  </el-link>
                </div>

                <div class="trace-section-block">
                  <div class="trace-block-label">内容</div>
                  <div class="trace-block-content trace-answer">{{ trace.answer }}</div>
                </div>

                <div class="trace-section-block">
                  <div class="trace-block-label">置信度分析</div>
                  <div class="confidence-bar">
                    <div class="confidence-bar-fill"
                         :class="'confidence-' + (trace.confidenceLevel || 'medium')"
                         :style="{ width: (trace.confidence * 100) + '%' }">
                    </div>
                    <span class="confidence-text">{{ (trace.confidence * 100).toFixed(1) }}%</span>
                  </div>
                </div>

                <div class="trace-section-block">
                  <div class="trace-block-label">匹配详情</div>
                  <div class="trace-meta-grid">
                    <div class="trace-meta-item" v-if="trace.vectorScore != null">
                      <span class="meta-label">向量相似度</span>
                      <span class="meta-value">{{ trace.vectorScore.toFixed(4) }}</span>
                    </div>
                    <div class="trace-meta-item" v-if="trace.rrfRank != null">
                      <span class="meta-label">RRF排序</span>
                      <span class="meta-value">#{{ trace.rrfRank }}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div v-if="!docTraces || docTraces.length === 0" class="trace-empty">
                <el-icon><Document /></el-icon>
                <span>暂无文档引用</span>
              </div>
            </el-tab-pane>

            <el-tab-pane label="知识图谱" name="graph">
              <div v-for="(trace, tIdx) in graphTraces" :key="'graph-'+tIdx"
                   class="trace-detail-card">
                <div class="trace-card-header">
                  <span class="trace-badge badge-medium">{{ tIdx + 1 }}</span>
                  <el-tag :type="confidenceTagType(trace.confidenceLevel)" size="small">
                    {{ confidenceLabel(trace.confidenceLevel) }}
                  </el-tag>
                  <div class="trace-card-actions">
                    <el-button v-if="trace.knowledgeId" type="primary" link size="small" @click="$emit('navigate-knowledge', trace)">
                      <el-icon><FolderOpened /></el-icon>
                      <span>知识库</span>
                    </el-button>
                    <el-button v-if="trace.documentId" type="success" link size="small" @click="handleViewDocument(trace)">
                      <el-icon><Document /></el-icon>
                      <span>查看原文</span>
                    </el-button>
                  </div>
                </div>

                <div class="trace-source-bar" v-if="trace.knowledgeName">
                  <span class="trace-source-label">来源知识库：</span>
                  <el-link type="primary" :underline="false" @click="$emit('navigate-knowledge', trace)">
                    {{ trace.knowledgeName }}
                    <el-icon class="el-icon--right"><ArrowRight /></el-icon>
                  </el-link>
                </div>

                <div class="trace-section-block">
                  <div class="trace-block-label">图谱关系</div>
                  <div class="trace-block-content trace-answer">{{ trace.answer }}</div>
                </div>

                <div class="trace-section-block">
                  <div class="trace-block-label">置信度分析</div>
                  <div class="confidence-bar">
                    <div class="confidence-bar-fill"
                         :class="'confidence-' + (trace.confidenceLevel || 'medium')"
                         :style="{ width: (trace.confidence * 100) + '%' }">
                    </div>
                    <span class="confidence-text">{{ (trace.confidence * 100).toFixed(1) }}%</span>
                  </div>
                </div>

                <div class="trace-section-block">
                  <div class="trace-block-label">匹配详情</div>
                  <div class="trace-meta-grid">
                    <div class="trace-meta-item" v-if="trace.rrfRank != null">
                      <span class="meta-label">RRF排序</span>
                      <span class="meta-value">#{{ trace.rrfRank }}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div v-if="!graphTraces || graphTraces.length === 0" class="trace-empty">
                <el-icon><Connection /></el-icon>
                <span>暂无图谱引用</span>
              </div>
            </el-tab-pane>
          </el-tabs>

          <!-- Feedback -->
          <div class="trace-feedback">
            <div class="trace-feedback-label">这个回答有帮助吗？</div>
            <div class="trace-feedback-actions">
              <button class="feedback-btn">👍 有用</button>
              <button class="feedback-btn">👎 需改进</button>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { computed } from 'vue'
import { ChatDotRound, Document, ArrowRight, FolderOpened, Connection } from '@element-plus/icons-vue'
import { overallConfidenceValue, overallConfidenceLevel, confidenceTagType, confidenceLabel, matchSourceLabel } from '@/modules/huyu-framework/backend/composables/useChatRender'

const props = defineProps({
  visible: { type: Boolean, default: false },
  msg: { type: Object, default: null },
  activeTab: { type: String, default: 'qa' },
  activeIndex: { type: Number, default: 0 },
  qaTraces: { type: Array, default: () => [] },
  docTraces: { type: Array, default: () => [] },
  graphTraces: { type: Array, default: () => [] }
})

const emit = defineEmits(['update:visible', 'update:activeTab', 'navigate-knowledge', 'view-source', 'view-document'])

// activeTab 是 prop，需通过 computed 实现 v-model 双向绑定
const activeTabModel = computed({
  get: () => props.activeTab,
  set: (v) => emit('update:activeTab', v)
})

/**
 * QA 命中的"源数据"记录 ID：优先 qaId；历史版本只写了 documentId
 * （QA 维度 documentId 与 qaId 同为 ai_knowledge_qa.id），兜底用它，避免按钮因 qaId 为空而消失。
 */
const qaRecordId = (trace) => trace?.qaId || (trace?.source === 'qa' ? trace?.documentId : null)

/** 查看原文：跳转到知识库并打开命中文档（需要所属文档 ID，DOC 命中已由后端归一） */
const handleViewDocument = (trace) => {
  if (!trace?.documentId) return
  emit('view-document', trace)
}
</script>

<style scoped>
.trace-panel {
  position: fixed;
  top: 0;
  right: 0;
  width: 380px;
  height: 100vh;
  background: #fff;
  box-shadow: -4px 0 24px rgba(0, 0, 0, 0.06);
  z-index: 2000;
  display: flex;
  flex-direction: column;
}

.slide-right-enter-active,
.slide-right-leave-active {
  transition: transform 0.3s ease;
}

.slide-right-enter-from,
.slide-right-leave-to {
  transform: translateX(100%);
}

.trace-panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 18px;
  border-bottom: 1px solid #ebeef5;
  background: #f6f8fa;
}

.trace-panel-title {
  font-size: 15px;
  font-weight: 600;
  color: #1f2328;
}

.trace-panel-close {
  background: none;
  border: none;
  font-size: 20px;
  color: #8b949e;
  cursor: pointer;
  line-height: 1;
  padding: 0;
}

.trace-panel-close:hover {
  color: #cf222e;
}

.trace-panel-body {
  flex: 1;
  overflow-y: auto;
  padding: 0;
}

.trace-overview {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  padding: 14px 18px;
  background: #f6f8fa;
  border-bottom: 1px solid #ebeef5;
}

.trace-stat {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.stat-label {
  font-size: 11px;
  color: #8b949e;
}

.stat-value {
  font-size: 16px;
  font-weight: 600;
  color: #1f2328;
}

.stat-value-sm {
  font-size: 12px;
  font-weight: 500;
  color: #656d76;
}

.text-high {
  color: #3fb950;
}

.text-medium {
  color: #d29922;
}

.text-low {
  color: #cf222e;
}

.trace-tabs {
  padding: 0 18px;
}

.trace-tabs :deep(.el-tabs__header) {
  margin: 0;
}

.trace-detail-card {
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  padding: 14px;
  margin-bottom: 10px;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.trace-card-active {
  border-color: #409eff;
  box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.12);
}

.trace-card-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}

.trace-card-actions {
  margin-left: auto;
  display: flex;
  gap: 4px;
}

.trace-source-bar {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 10px;
  margin-bottom: 10px;
  background: #f6f8fa;
  border-radius: 6px;
  border: 1px solid #ebeef5;
  font-size: 12px;
}

.trace-source-label {
  color: #656d76;
  white-space: nowrap;
}

.trace-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  font-size: 11px;
  font-weight: bold;
  color: white;
}

.badge-high {
  background: #3fb950;
}

.badge-medium {
  background: #d29922;
}

.badge-low {
  background: #cf222e;
}

.trace-section-block {
  margin-bottom: 10px;
}

.trace-block-label {
  font-size: 11px;
  font-weight: 600;
  color: #8b949e;
  margin-bottom: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.trace-block-content {
  font-size: 13px;
  color: #1f2328;
  line-height: 1.6;
}

.trace-answer {
  background: #f6f8fa;
  padding: 8px 10px;
  border-radius: 4px;
  border-left: 3px solid #8b949e;
}

.confidence-bar {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 4px;
}

.confidence-bar-fill {
  height: 6px;
  border-radius: 3px;
  transition: width 0.3s ease;
  min-width: 20px;
}

.confidence-high {
  background: #3fb950;
}

.confidence-medium {
  background: #d29922;
}

.confidence-low {
  background: #cf222e;
}

.confidence-text {
  font-size: 12px;
  color: #656d76;
  font-weight: 500;
  white-space: nowrap;
}

.trace-meta-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px 12px;
}

.trace-meta-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.meta-label {
  font-size: 11px;
  color: #8b949e;
}

.meta-value {
  font-size: 12px;
  color: #1f2328;
  font-weight: 500;
}

.trace-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 40px 0;
  color: #d0d7de;
  font-size: 13px;
}

.trace-feedback {
  padding: 14px 18px;
  border-top: 1px solid #ebeef5;
  background: #f6f8fa;
}

.trace-feedback-label {
  font-size: 12px;
  color: #8b949e;
  margin-bottom: 8px;
}

.trace-feedback-actions {
  display: flex;
  gap: 8px;
}

.feedback-btn {
  padding: 6px 14px;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
  font-size: 12px;
  color: #656d76;
  transition: all 0.2s;
}

.feedback-btn:hover {
  border-color: #409eff;
  color: #409eff;
}
</style>
